import PropTypes from 'prop-types';
import {
  NewBookingConfigPropType,
  NewBookingDeliveryDetailPropType,
} from 'common/prop-types';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import usePrevious from 'common/usePrevious';
import _get from 'lodash/get';
import _minBy from 'lodash/minBy';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';
import { getUserCountryCodeFromState } from 'selectors/app-selector';
import { connect } from 'react-redux';
import {
  PickUpTimeSectionUI,
  bestOptionsKey,
  generateTimeSlotInDate,
} from '../common-section-ui/pick-up-time-section-ui';
import useSelectDefaultDateTab from '../common-logic/use-default-tab';
import useChooseDefaultSlotInTab from '../common-logic/use-choose-default-slot-in-tab';

const PickUpTimeSection = ({
  dispatch,
  deliveryID,
  deliveryDetail,
  fetchingQuote,
  config,
  countryCode,
}) => {
  const prevFetchingQuote = usePrevious(fetchingQuote);
  const justFetchedQuote = prevFetchingQuote && !fetchingQuote;
  const selectedServiceType = _get(deliveryDetail, 'configuration.serviceType');
  const selectedSpecialServiceType = _get(
    deliveryDetail,
    'configuration.specialServiceType',
  );
  const scheduledPickupSlot = _get(
    deliveryDetail,
    'configuration.scheduledPickupSlot',
  );

  const onTimeSlotClick = useCallback(
    timeSlot => () => {
      const scheduledPickupSlot =
        timeSlot && timeSlot.from && timeSlot.to ? timeSlot : null;
      dispatch(
        NewBookingDuck.creators.updateServiceConfig(deliveryID, {
          scheduledPickupSlot,
        }),
      );

      dispatch(
        ScribeDuck.creators.scribeExpressDeliverySelectPickupTime(
          scheduledPickupSlot,
        ),
      );
    },
    [dispatch, deliveryID],
  );

  const timeSlots = _get(config, 'scheduleInfo.timeSlots', []);
  const bestOptions = _get(config, 'scheduleInfo.bestOptions', []);
  const enablePickUpNow = _get(config, 'scheduleInfo.enablePickUpNow', false);
  const sameDayNowSlotPrice = useMemo(
    () =>
      selectedServiceType === 'SAME_DAY' &&
      _get(
        bestOptions.find(timeSlot => !timeSlot.from && !timeSlot.to),
        'fare',
      ),
    [selectedServiceType, bestOptions],
  );
  const timeSlotsInTabs = useMemo(
    () => {
      const timeSlotsInTabs = {};

      const serviceType = config.serviceTypes.find(
        st => st.id === selectedServiceType,
      );
      const pickUpSLA = _get(serviceType, 'metadata.pickUpSLA', 0);

      if (bestOptions) {
        bestOptions.forEach(timeSlot =>
          generateTimeSlotInDate(
            timeSlot,
            pickUpSLA,
            true,
            countryCode,
            scheduledPickupSlot,
            sameDayNowSlotPrice,
            timeSlotsInTabs,
            onTimeSlotClick,
          ),
        );
      }
      if (enablePickUpNow) {
        generateTimeSlotInDate(
          { available: true },
          pickUpSLA,
          false,
          countryCode,
          scheduledPickupSlot,
          sameDayNowSlotPrice,
          timeSlotsInTabs,
          onTimeSlotClick,
        );
      }
      timeSlots.forEach(timeSlot =>
        generateTimeSlotInDate(
          timeSlot,
          0,
          false,
          countryCode,
          scheduledPickupSlot,
          sameDayNowSlotPrice,
          timeSlotsInTabs,
          onTimeSlotClick,
        ),
      );

      return timeSlotsInTabs;
    },
    [
      selectedServiceType,
      enablePickUpNow,
      timeSlots,
      scheduledPickupSlot,
      bestOptions,
      sameDayNowSlotPrice,
    ],
  );

  const bestOptionsTimeSlot = _get(timeSlotsInTabs, [
    bestOptionsKey,
    'timeSlots',
  ]);

  const defaultBestOptionTimeSlot = useMemo(
    () => {
      if (bestOptionsTimeSlot) {
        return _minBy(bestOptionsTimeSlot, s => s.fare);
      }
      return undefined;
    },
    [bestOptionsTimeSlot],
  );

  // select date or first date
  const [selectedTab, setSelectedTab] = useState(undefined);
  useSelectDefaultDateTab(
    timeSlots,
    timeSlotsInTabs,
    selectedTab,
    scheduledPickupSlot,
    setSelectedTab,
  );

  const selectDefaultSlotInTab = useChooseDefaultSlotInTab(
    timeSlotsInTabs,
    onTimeSlotClick,
    defaultBestOptionTimeSlot,
  );

  // change a date, auto select first slot
  const onSelectTab = useCallback(
    tabKey => {
      setSelectedTab(tabKey);
      selectDefaultSlotInTab(tabKey);
      dispatch(
        ScribeDuck.creators.scribeExpressDeliverySelectPickupDate(tabKey),
      );
    },
    [setSelectedTab, selectDefaultSlotInTab, dispatch],
  );

  // select default slot
  useEffect(
    () => {
      if (scheduledPickupSlot === undefined) {
        if (selectedTab) {
          // when there is any tab, pick a default slot
          selectDefaultSlotInTab(selectedTab);
        } else if (justFetchedQuote && timeSlots.length === 0) {
          // when there is no timeslots, select now
          onTimeSlotClick(null)();
        }
      }
    },
    [
      scheduledPickupSlot,
      selectedTab,
      selectDefaultSlotInTab,
      onTimeSlotClick,
      timeSlots,
      justFetchedQuote,
    ],
  );

  // clear unavailable slot
  useEffect(
    () => {
      if (scheduledPickupSlot !== undefined) {
        let needToClear = true;
        if (scheduledPickupSlot === null) {
          needToClear = !enablePickUpNow;
        } else {
          timeSlots.forEach(timeSlot => {
            if (
              timeSlot.available &&
              scheduledPickupSlot.from === timeSlot.from &&
              scheduledPickupSlot.to === timeSlot.to
            ) {
              needToClear = false;
            }
          });
        }

        if (needToClear) {
          dispatch(
            NewBookingDuck.creators.removeScheduledPickupSlot(deliveryID),
          );
        }
      }
    },
    [enablePickUpNow, scheduledPickupSlot, timeSlots, deliveryID, dispatch],
  );

  if (
    !selectedServiceType ||
    !selectedSpecialServiceType ||
    _get(timeSlots, 'length') === 0 ||
    Object.keys(timeSlotsInTabs).length === 0
  ) {
    return null;
  }

  return (
    <PickUpTimeSectionUI
      timeSlotsInTabs={timeSlotsInTabs}
      selectedTab={selectedTab}
      onSelectTab={onSelectTab}
    />
  );
};

PickUpTimeSection.propTypes = {
  config: NewBookingConfigPropType.isRequired,
  deliveryDetail: NewBookingDeliveryDetailPropType.isRequired,
  deliveryID: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  fetchingQuote: PropTypes.bool.isRequired,
  countryCode: PropTypes.string.isRequired,
};

export default connect(state => ({
  countryCode: getUserCountryCodeFromState(state),
}))(PickUpTimeSection);
